import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Copy } from 'lucide-react';
import { Reveal } from '../../components/motion';
import './tools.css';

function CopyButton({ value, disabled }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (disabled) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button type="button" className="tool-copy" onClick={copy} disabled={disabled}>
      {copied ? <Check size={15} /> : <Copy size={15} />}{copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export function ToolShell({ eyebrow, title, description, children, aside }) {
  return (
    <>
      <section className="tool-page-hero">
        <Reveal className="tool-page-hero-inner">
          <Link to="/free-tools" className="tool-back"><ArrowLeft size={15} /> All free tools</Link>
          <div className="section-tag">{eyebrow}</div>
          <h1>{title}</h1>
          <p>{description}</p>
        </Reveal>
      </section>
      <section className="tool-workspace-shell">
        <div className="tool-workspace-grid">
          <Reveal className="tool-card-3d">{children}</Reveal>
          {aside && <Reveal className="tool-aside" delay={90}>{aside}</Reveal>}
        </div>
      </section>
    </>
  );
}

export { CopyButton };

const SCHEMA_TYPES = ['LocalBusiness', 'Restaurant', 'Dentist', 'HVACBusiness', 'Plumber', 'RoofingContractor', 'AutoRepair', 'HairSalon', 'LegalService', 'MedicalBusiness'];

export function SchemaGenerator() {
  const [form, setForm] = useState({
    type: 'LocalBusiness', name: '', phone: '', url: '', street: '', city: '', region: 'FL', postal: '', priceRange: '$$',
  });
  const change = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));

  const json = useMemo(() => {
    const data = {
      '@context': 'https://schema.org',
      '@type': form.type,
      name: form.name || 'Your Business Name',
      ...(form.phone ? { telephone: form.phone } : {}),
      ...(form.url ? { url: form.url } : {}),
      ...(form.priceRange ? { priceRange: form.priceRange } : {}),
      address: {
        '@type': 'PostalAddress',
        ...(form.street ? { streetAddress: form.street } : {}),
        ...(form.city ? { addressLocality: form.city } : {}),
        ...(form.region ? { addressRegion: form.region } : {}),
        ...(form.postal ? { postalCode: form.postal } : {}),
        addressCountry: 'US',
      },
    };
    return `<script type="application/ld+json">\n${JSON.stringify(data, null, 2)}\n</script>`;
  }, [form]);

  return (
    <ToolShell
      eyebrow="FREE TOOL · STRUCTURED DATA"
      title="Local Schema Generator"
      description="Structured data tells Google and AI assistants what your business is, where it is, and how to reach it. Fill this in and paste the result into your site's <head>."
      aside={(
        <div className="tool-output">
          <div className="tool-output-head"><span className="dt-label" style={{ color: 'var(--muted)' }}>Copy into your site &lt;head&gt;</span><CopyButton value={json} disabled={!form.name} /></div>
          <pre className="tool-code">{json}</pre>
          <p className="tool-hint">Paste once. Search engines re-read it on their next crawl. Nothing here is stored.</p>
        </div>
      )}
    >
      <div className="tool-form">
        <label className="tool-field"><span>Business type</span>
          <select name="type" value={form.type} onChange={change}>{SCHEMA_TYPES.map(type => <option key={type}>{type}</option>)}</select>
        </label>
        <label className="tool-field"><span>Business name</span><input name="name" value={form.name} onChange={change} placeholder="Premier HVAC Solutions" /></label>
        <div className="tool-field-row">
          <label className="tool-field"><span>Phone</span><input name="phone" value={form.phone} onChange={change} placeholder="(555) 123-4567" /></label>
          <label className="tool-field"><span>Website</span><input name="url" value={form.url} onChange={change} placeholder="https://example.com" /></label>
        </div>
        <label className="tool-field"><span>Street address</span><input name="street" value={form.street} onChange={change} placeholder="123 Main St" /></label>
        <div className="tool-field-row">
          <label className="tool-field"><span>City</span><input name="city" value={form.city} onChange={change} placeholder="Coral Springs" /></label>
          <label className="tool-field"><span>State</span><input name="region" value={form.region} onChange={change} placeholder="FL" /></label>
          <label className="tool-field"><span>ZIP</span><input name="postal" value={form.postal} onChange={change} placeholder="33065" /></label>
        </div>
        <label className="tool-field"><span>Price range</span>
          <select name="priceRange" value={form.priceRange} onChange={change}><option>$</option><option>$$</option><option>$$$</option><option>$$$$</option></select>
        </label>
      </div>
    </ToolShell>
  );
}

const CHANNELS = ['facebook', 'instagram', 'google', 'email', 'sms', 'qr', 'flyer'];

export function UtmBuilder() {
  const [form, setForm] = useState({ url: '', source: 'facebook', medium: 'social', campaign: '' });
  const change = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));

  const link = useMemo(() => {
    if (!form.url) return '';
    let base = form.url.trim();
    if (!/^https?:\/\//i.test(base)) base = `https://${base}`;
    try {
      const url = new URL(base);
      if (form.source) url.searchParams.set('utm_source', form.source.trim());
      if (form.medium) url.searchParams.set('utm_medium', form.medium.trim());
      if (form.campaign) url.searchParams.set('utm_campaign', form.campaign.trim().replace(/\s+/g, '_').toLowerCase());
      return url.href;
    } catch {
      return '';
    }
  }, [form]);

  return (
    <ToolShell
      eyebrow="FREE TOOL · ATTRIBUTION"
      title="Campaign Link Builder"
      description="Add tracking tags to any link so your analytics can tell you which post, email, or ad actually produced the customer — instead of guessing."
      aside={(
        <div className="tool-output">
          <div className="tool-output-head"><span className="dt-label" style={{ color: 'var(--muted)' }}>Your tracking link</span><CopyButton value={link} disabled={!link} /></div>
          <div className="tool-link-out">{link || 'Enter a destination URL to build your link.'}</div>
          <p className="tool-hint">Use one campaign name per promotion so results group together in your reports.</p>
        </div>
      )}
    >
      <div className="tool-form">
        <label className="tool-field"><span>Destination URL</span><input name="url" value={form.url} onChange={change} placeholder="yourbusiness.com/spring-offer" /></label>
        <label className="tool-field"><span>Source (where it is posted)</span>
          <input name="source" value={form.source} onChange={change} list="utm-channels" placeholder="facebook" />
          <datalist id="utm-channels">{CHANNELS.map(channel => <option key={channel} value={channel} />)}</datalist>
        </label>
        <label className="tool-field"><span>Medium (type of link)</span><input name="medium" value={form.medium} onChange={change} placeholder="social" /></label>
        <label className="tool-field"><span>Campaign name</span><input name="campaign" value={form.campaign} onChange={change} placeholder="spring ac tune-up" /></label>
      </div>
    </ToolShell>
  );
}

export function ReviewLinkBuilder() {
  const [placeId, setPlaceId] = useState('');
  const trimmed = placeId.trim();
  const reviewLink = trimmed ? `https://search.google.com/local/writereview?placeid=${encodeURIComponent(trimmed)}` : '';
  const qrSrc = reviewLink ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(reviewLink)}` : '';

  return (
    <ToolShell
      eyebrow="FREE TOOL · REPUTATION"
      title="Google Review Link Builder"
      description="Fewer clicks means more reviews. Turn your Google Place ID into a direct link that opens the review box, plus a QR code for receipts and counters."
      aside={(
        <div className="tool-output">
          <div className="tool-output-head"><span className="dt-label" style={{ color: 'var(--muted)' }}>Direct review link</span><CopyButton value={reviewLink} disabled={!reviewLink} /></div>
          <div className="tool-link-out">{reviewLink || 'Paste your Place ID to build the link.'}</div>
          {qrSrc && <div className="tool-qr"><img src={qrSrc} alt="Google review QR code" width="180" height="180" /><span>Scan to leave a review</span></div>}
          <p className="tool-hint">Find your Place ID with Google's Place ID Finder, then text this link after every job.</p>
        </div>
      )}
    >
      <div className="tool-form">
        <label className="tool-field"><span>Google Place ID</span><input value={placeId} onChange={event => setPlaceId(event.target.value)} placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4" /></label>
        <p className="tool-hint">Your Place ID looks like <code>ChIJ…</code>. Search “Google Place ID Finder”, select your business, and paste the ID here. The QR code is generated on the fly and never stored.</p>
      </div>
    </ToolShell>
  );
}

const CHECKLIST = [
  { group: 'Google Business Profile', items: ['Profile is claimed and verified', 'Primary category is correct', 'Hours are accurate, including holidays', 'At least 10 recent photos uploaded', 'Products or services are listed'] },
  { group: 'Website', items: ['Site loads over HTTPS', 'Every page has a unique title and meta description', 'Business name, address, and phone match Google exactly', 'LocalBusiness schema is present', 'Mobile-friendly and loads quickly'] },
  { group: 'Reviews & Reputation', items: ['A steady flow of new reviews each month', 'Owner responds to reviews within 48 hours', 'Review link shared after every job'] },
  { group: 'Local Content & Links', items: ['A page for each core service', 'A page or section for each city served', 'Listed consistently on major directories', 'Some local backlinks or sponsorships', 'Social profiles linked from the website'] },
];

export function SeoChecklist() {
  const total = CHECKLIST.reduce((sum, group) => sum + group.items.length, 0);
  const [done, setDone] = useState(() => new Set());
  const toggle = key => setDone(current => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const percent = Math.round((done.size / total) * 100);

  return (
    <ToolShell
      eyebrow="FREE TOOL · SELF AUDIT"
      title="Local SEO Checklist"
      description="The 18 things that actually move local rankings, in plain English. Tick each one to see how ready your business is — no account, nothing saved."
      aside={(
        <div className="tool-output tool-score-panel">
          <div className="tool-score-ring" style={{ '--score': `${percent * 3.6}deg` }}><span>{percent}%</span></div>
          <strong>{done.size} of {total} complete</strong>
          <p className="tool-hint">{percent === 100 ? 'Strong foundation. Now track rankings to prove it.' : percent >= 60 ? 'Solid start — close the gaps above.' : 'Plenty of quick wins available here.'}</p>
          <Link to="/signup" className="btn-primary">Track this over time →</Link>
        </div>
      )}
    >
      <div className="tool-checklist">
        {CHECKLIST.map(group => (
          <div className="tool-checklist-group" key={group.group}>
            <h3>{group.group}</h3>
            {group.items.map(item => {
              const key = `${group.group}:${item}`;
              return (
                <label className={`tool-check${done.has(key) ? ' is-done' : ''}`} key={key}>
                  <input type="checkbox" checked={done.has(key)} onChange={() => toggle(key)} />
                  <span>{item}</span>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </ToolShell>
  );
}
